import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../../auth/core/AuthErrorInterceptor'

/**
 * Client for the JobService job-logs endpoint, `GET /v1/sessions/{sessionId}/logs`
 * (`JobService/lambda/code/python/session/logs.py` in omicscloud-cdk). It reads the job's progress
 * streams in `/cloud/jobs/progress` server side, and only for the job's owner: the browser no
 * longer reads CloudWatch with the Identity Pool role, which could read every user's streams.
 */
const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

/** Most events the endpoint returns across a session's step streams in one call. */
export const MAX_STEP_EVENTS = 2000

export interface LogEvent {
  timestamp: number
  message: string
}

/** A step's stream, e.g. `<session>/1-uuid/stdout`, shown as `1-uuid/stdout`. */
export interface LogStream {
  name: string
  displayName: string
  events: LogEvent[]
}

export interface SessionLogs {
  sessionId: string
  /** The session's own stream (the job's progress messages), oldest first. */
  sessionEvents: LogEvent[]
  /** The per-step streams under the session, excluding its own stream. */
  streams: LogStream[]
  /** Newest event timestamp in this answer, or `after` when there is none: the next `after`. */
  latestTimestamp: number
  /** The step budget (`limit`) ran out before every step stream was read. */
  limitReached: boolean
  /**
   * False when the endpoint answered 404: the session doesn't exist or isn't the caller's. The
   * rest of the result is then empty.
   */
  found: boolean
}

export interface SessionLogsQuery {
  /** Only events newer than this (ms). Default 0. */
  after?: number
  /** Events across the step streams; default and max 2000. 0 skips the step streams. */
  limit?: number
  /** Events of the session's own stream; default 100, max 1000. 0 skips it. */
  sessionLimit?: number
}

interface SessionLogsResponse {
  sessionId?: string
  session?: {events?: unknown}
  streams?: unknown
  latestTimestamp?: unknown
  limitReached?: unknown
}

function toEvents(value: unknown): LogEvent[] {
  if (!Array.isArray(value)) return []
  return value.map((e) => ({
    timestamp: Number(e?.timestamp) || 0,
    message: typeof e?.message === 'string' ? e.message : '',
  }))
}

function toStreams(value: unknown): LogStream[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((s) => typeof s?.name === 'string' && s.name)
    .map((s) => ({
      name: s.name,
      displayName: typeof s.displayName === 'string' && s.displayName ? s.displayName : s.name,
      events: toEvents(s.events),
    }))
}

function emptySessionLogs(sessionId: string, after: number): SessionLogs {
  return {
    sessionId,
    sessionEvents: [],
    streams: [],
    latestTimestamp: after,
    limitReached: false,
    found: false,
  }
}

function errorStatus(error: unknown): number | undefined {
  const status = (error as {response?: {status?: unknown}} | undefined)?.response?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * `GET /v1/sessions/{sessionId}/logs`. A 404 resolves to an empty result with `found: false`;
 * any other failure (5xx, network, an expired session) rejects.
 */
export async function fetchSessionLogs(
  sessionId: string,
  query: SessionLogsQuery = {}
): Promise<SessionLogs> {
  const after = query.after ?? 0
  const queryStringParameters: Record<string, string> = {after: String(after)}
  if (query.limit !== undefined) queryStringParameters.limit = String(query.limit)
  if (query.sessionLimit !== undefined) {
    queryStringParameters.sessionLimit = String(query.sessionLimit)
  }

  let response: SessionLogsResponse
  try {
    response = await apiCallWithAuthCheck(() =>
      API.get(apiName, `v1/sessions/${encodeURIComponent(sessionId)}/logs`, {
        queryStringParameters,
      })
    )
  } catch (error) {
    if (errorStatus(error) === 404) return emptySessionLogs(sessionId, after)
    throw error
  }

  const latest = Number(response?.latestTimestamp)
  return {
    sessionId,
    sessionEvents: toEvents(response?.session?.events),
    streams: toStreams(response?.streams),
    latestTimestamp: Number.isFinite(latest) && latest > after ? latest : after,
    limitReached: response?.limitReached === true,
    found: true,
  }
}

// ---------------------------------------------------------------------------
// Step streams as the Session Logs tab shows them
// ---------------------------------------------------------------------------

export interface LogStreamPanel {
  streamName: string
  displayName: string
  messages: LogEvent[]
  expanded: boolean
}

/** Panels for the streams that have events, expanded. */
export function toStreamPanels(streams: LogStream[]): LogStreamPanel[] {
  return streams
    .filter((s) => s.events.length > 0)
    .map((s) => ({
      streamName: s.name,
      displayName: s.displayName,
      messages: s.events,
      expanded: true,
    }))
}

/** Total events across the streams. */
export function countStreamEvents(streams: LogStream[]): number {
  return streams.reduce((total, s) => total + s.events.length, 0)
}

/**
 * A refresh's panels merged into the shown ones: new events are appended to their stream (which
 * keeps its expanded state), and streams not shown yet are added at the end.
 */
export function mergeStreamPanels(
  existing: LogStreamPanel[],
  incoming: LogStreamPanel[]
): LogStreamPanel[] {
  const merged = [...existing]
  for (const inc of incoming) {
    const idx = merged.findIndex((s) => s.streamName === inc.streamName)
    if (idx >= 0) {
      merged[idx] = {...merged[idx], messages: [...merged[idx].messages, ...inc.messages]}
    } else {
      merged.push(inc)
    }
  }
  return merged
}

// ---------------------------------------------------------------------------
// The session's own stream as the Job Messages tab shows it
// ---------------------------------------------------------------------------

function formatMinute(ts: number): string {
  return new Date(ts).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

/**
 * One `[time] message` line per event. Jobs write each progress event as JSON with a `message`
 * field; anything else is shown as written.
 */
export function formatProgressMessages(events: LogEvent[]): string {
  return events
    .map((event) => {
      const time = event.timestamp ? formatMinute(event.timestamp) : 'Unknown time'
      let message = 'No message'
      if (event.message) {
        try {
          const parsed = JSON.parse(event.message)
          message = parsed?.message || 'No message in parsed JSON'
        } catch {
          message = event.message
        }
      }
      return `[${time}] ${message}`
    })
    .join('\n')
}
